/* The conversation's shared treatments: the card, the three buttons, a question's option, the
 * shimmering status line, the sunken notice and the two entrances.
 *
 * Canon: `docs/design/ai-buddy-2026-09-30/`, board O ("raised surface family") with M for the flow.
 * Every colour, shadow and duration is a token (`--buddy-*`, `--shadow-buddy-*` in
 * `tokens/colors.css`, `--dur-buddy-*` in `tokens/spacing.css`); sizes are layout.
 *
 * ── Flat, calm surfaces (owner, 29 October and 4 November 2026) ────────────────────────────────
 * The primary is a soft gradient with no cast and no bevel (`--buddy-primary-fill`, built from the
 * brand's own gradient stops, so it follows the trainer's brand). Since 4 November 2026 (owner) it
 * is the app's one primary paint, the Pulpit pill's diagonal gradient, and the composer's round
 * send button is flat too. Since the P5 boards the card, the secondary and a question's option are
 * flat as well: one hairline, no shadow.
 *
 * The primary is NOT `shared/ui/emberButton`: composing that class and overriding it here would be
 * settled by chunk order, which differs between the dev server and the build. */

.card {
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

/* The buttons (P5 boards, owner, 4 November 2026): ONE secondary, a hairline pill; ONE ghost, the
 * words alone; and the primary, the ember fill with its white label (the owner's own exception, not
 * to be "fixed" for contrast). All flat, all 500, and each with a hover, a press and a visible
 * keyboard focus. Only the composer's pill keeps the raised 3D; its round send is flat. */
.button {
  display: inline-flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  height: 36px;
  padding: var(--space-0) var(--space-14px);
  font-family: var(--font-body);
  font-size: var(--fs-foot);
  font-weight: var(--fw-medium);
  color: var(--text);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
  transition-duration: var(--dur-fast);
  transition-property: background-color, color, filter;
  transition-timing-function: var(--ease);
}

.button:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.button:hover:not([aria-disabled='true']) {
  background: var(--hover-tint);
}

.button:active:not([aria-disabled='true']) {
  background: var(--bg-chip);
}

.ghost {
  padding: var(--space-0) var(--space-10px);
  color: var(--text-2);
  border-color: transparent;
}

.ghost:hover:not([aria-disabled='true']) {
  color: var(--text);
}

.primary {
  padding: var(--space-0) var(--space-4);
  color: var(--on-accent-grad);
  background: var(--buddy-primary-fill);
  border-color: transparent;
  box-shadow: none;

  /* White on the accent clears 3:1 only as bold text (system spec amendment, 2 November 2026). */
  font-weight: var(--fw-semibold);
}

.primary:hover:not([aria-disabled='true']) {
  background: var(--buddy-primary-fill);
  filter: var(--cta-lift);
}

.primary:active:not([aria-disabled='true']) {
  background: var(--buddy-primary-fill);
  filter: var(--cta-press);
}

.small {
  height: 32px;
  padding: var(--space-0) var(--space-3);
}

/* Refusing, and still focusable: the reason it names is read with it (`aria-describedby`). */
.button[aria-disabled='true'] {
  cursor: default;
  opacity: 0.4;
}

/* A refusing primary is the chip, not a faded ember: its label stays readable. */
.primary[aria-disabled='true'] {
  color: var(--text-2);
  background: var(--bg-chip);
  border-color: var(--border);
  opacity: 1;
}

/* One answer to a clarifying question: a native radio inside, so the group has one tab stop and
 * the arrows walk it. */
.option {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: var(--space-7px) var(--space-13px);
  line-height: var(--lh-hint);
  overflow-wrap: anywhere;
  font-size: var(--fs-foot);
  color: var(--text);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
  transition-duration: var(--dur-base);
  transition-property: color, background-color, border-color;
  transition-timing-function: var(--ease);
}

.option:hover {
  background: var(--hover-tint);
}

.option:active {
  background: var(--bg-chip);
}

.optionOn,
.optionOn:hover {
  color: var(--bg);
  background: var(--text);
  border-color: var(--text);
}

.option:has(input:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* A line being worked on: the tertiary ink with a sheen running through it. */
.shimmer {
  color: transparent;
  background: var(--buddy-shimmer);
  background-clip: text;
  background-size: 200% 100%;
  animation-name: shimmer;
  animation-duration: var(--dur-buddy-shimmer);
  animation-timing-function: var(--ease-linear);
  animation-iteration-count: infinite;
}

.notice {
  display: flex;
  gap: var(--space-10px);
  align-items: flex-start;
  padding: var(--space-13px) var(--space-15px);
  font-size: var(--fs-foot);
  line-height: var(--lh-lede);
  color: var(--text-2);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-md);
}

.notice b {
  color: var(--text);
}

.noticeGlyph {
  flex: none;
  width: 16px;
  height: 16px;
  margin-top: var(--space-1px);
}

/* A message rising in, and a card a beat slower, as the canon's `up` keyframe. They play when the
 * element is first drawn and never again: every message box is keyed by its turn. */
.rise {
  animation-name: rise;
  animation-duration: var(--dur-slow);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.riseCard {
  animation-name: rise;
  animation-duration: var(--dur-buddy-card);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

/* A message already there at the thread's first paint (a resumed conversation) stands still. */
[data-still].rise,
[data-still] .rise,
[data-still] .riseCard {
  animation-name: none;
}

/* Work under way: the build log's running line, a file being read. */
.spinner {
  flex: none;
  width: 14px;
  height: 14px;
  border: var(--border-thick) solid var(--border-2);
  border-top-color: var(--text);
  border-radius: var(--radius-sm);
  animation-name: spin;
  animation-duration: var(--dur-buddy-spin);
  animation-timing-function: var(--ease-linear);
  animation-iteration-count: infinite;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes shimmer {
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
}

@keyframes spin {
  to {
    transform: rotate(1turn);
  }
}
